import { STEP_MILESTONE_ICON_CENTER } from './step-map-metrics'

type Point = { x: number; y: number }
type Side = 'leading' | 'trailing'
export type ConnectorSpan = 'solid' | 'dashed'

const CORNER_RADIUS = 64

// One smooth corner in units of the radius, from the exported vector.
const CORNER_PROFILE: [Point, Point, Point][] = [
  [
    { x: 0.64978, y: 0 },
    { x: 0.47495, y: 0 },
    { x: 0.34126, y: 0.06812 },
  ],
  [
    { x: 0.22365, y: 0.12804 },
    { x: 0.12804, y: 0.22365 },
    { x: 0.06812, y: 0.34126 },
  ],
  [
    { x: 0, y: 0.47495 },
    { x: 0, y: 0.64997 },
    { x: 0, y: 1 },
  ],
]

interface ConnectorInput {
  nodes: Point[]
  width: number
  height: number
  completedNodeCount: number
}

function spanBoundary(
  completed: number,
  nodeCount: number,
  totalCount: number,
): number {
  if (completed <= 0) return 0
  if (completed >= nodeCount) return totalCount - 1
  return Math.min(completed - 1, totalCount - 1)
}

export function spanPoints(
  { nodes, height, completedNodeCount }: ConnectorInput,
  span: ConnectorSpan,
): Point[] {
  if (nodes.length === 0) return []
  const points = [...nodes]
  const milestone = {
    x: STEP_MILESTONE_ICON_CENTER,
    y: height - STEP_MILESTONE_ICON_CENTER,
  }
  if (milestone.y > nodes[nodes.length - 1].y) points.push(milestone)
  const boundary = spanBoundary(completedNodeCount, nodes.length, points.length)
  if (span === 'solid') return boundary > 0 ? points.slice(0, boundary + 1) : []
  return points.slice(boundary)
}

const fmt = (p: Point) => `${+p.x.toFixed(3)} ${+p.y.toFixed(3)}`

function smoothCorner(
  corner: Point,
  approach: Point,
  exit: Point,
  radius: number,
): string {
  const at = (o: Point) => ({
    x: corner.x + (approach.x * o.x + exit.x * o.y) * radius,
    y: corner.y + (approach.y * o.x + exit.y * o.y) * radius,
  })
  return CORNER_PROFILE.map(
    ([c1, c2, end]) => `C ${fmt(at(c1))} ${fmt(at(c2))} ${fmt(at(end))}`,
  ).join(' ')
}

// Centred nodes alternate sides, so the snake never loops out the same margin twice.
function sweepSide(
  from: Point,
  to: Point,
  mid: number,
  last: Side | null,
): Side {
  if (from.x !== mid) return from.x > mid ? 'trailing' : 'leading'
  if (to.x !== mid) return to.x > mid ? 'trailing' : 'leading'
  return last === 'leading' ? 'trailing' : 'leading'
}

function sweep(from: Point, to: Point, width: number, side: Side): string {
  if (to.y === from.y) return `L ${fmt(to)}`
  const edge = side === 'trailing' ? width : 0
  const run = Math.abs(to.y - from.y)
  const radius = (x: number) =>
    Math.min(CORNER_RADIUS, Math.abs(x - edge), run / 2)
  const entry = radius(from.x)
  const exit = radius(to.x)
  const t = edge > from.x ? 1 : -1
  const d = to.y > from.y ? 1 : -1
  return [
    `L ${fmt({ x: edge - t * entry, y: from.y })}`,
    smoothCorner(
      { x: edge, y: from.y },
      { x: -t, y: 0 },
      { x: 0, y: d },
      entry,
    ),
    `L ${fmt({ x: edge, y: to.y - d * exit })}`,
    smoothCorner({ x: edge, y: to.y }, { x: 0, y: -d }, { x: -t, y: 0 }, exit),
    `L ${fmt(to)}`,
  ].join(' ')
}

export function connectorPath(
  input: ConnectorInput,
  span: ConnectorSpan,
): { d: string; minY: number; maxY: number } | null {
  const points = spanPoints(input, span)
  if (points.length === 0) return null
  const mid = input.width / 2
  const parts = [`M ${fmt(points[0])}`]
  let lastSide: Side | null = null
  for (let i = 1; i < points.length; i++) {
    const from = points[i - 1]
    const to = points[i]
    const side = sweepSide(from, to, mid, lastSide)
    parts.push(sweep(from, to, input.width, side))
    if (from.y !== to.y) lastSide = side
  }
  const ys = points.map((p) => p.y)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  // A span with no vertical run ramps over the whole column.
  return maxY > minY
    ? { d: parts.join(' '), minY, maxY }
    : { d: parts.join(' '), minY: 0, maxY: input.height }
}
